import { Extension } from '@tiptap/core'
import { Plugin, PluginKey } from 'prosemirror-state'
import { Decoration, DecorationSet } from 'prosemirror-view'
import { EventBus } from '@/eventBus/eventBus.js'
import i18n from '@/i18n/i18n'

// Helper function to show menu
function showMenu(event, position, nodeType) {
  // Create menu element
  const menu = document.createElement('div')
  menu.className = 'menu-tooltip'
  menu.style.position = 'fixed'
  menu.style.background = 'white'
  menu.style.border = '1px solid #ddd'
  menu.style.borderRadius = '8px'
  menu.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)'
  menu.style.zIndex = '9999'
  menu.style.minWidth = '120px'
  menu.style.padding = '4px 0'
  
  // Position the menu
  const rect = event.target.getBoundingClientRect()
  const menuHeight = 200 // Approximate menu height
  const windowHeight = window.innerHeight
  
  // Check if menu would be clipped at bottom
  if (rect.top + menuHeight > windowHeight) {
    // Position above the button
    menu.style.top = (rect.top - menuHeight - 10) + 'px'
  } else {
    // Position below the button (original behavior)
    menu.style.top = (rect.top - 80) + 'px'
  }
  
  menu.style.left = (rect.left - 60) + 'px'
  
  // Add menu items
  const menuItems = [
    { text: i18n.t('DefaultManual'), icon: 'fas fa-plus', action: 'addStep', subtype: 'default' },
    { text: `${i18n.t('Warning')} ${i18n.t('Step')}`, icon: 'fas fa-exclamation-triangle', action: 'addStep', subtype: 'warning' },
    { text: `${i18n.t('ErrorStep')} ${i18n.t('Step')}`, icon: 'fas fa-times-circle', action: 'addStep', subtype: 'error' },
    { text: `${i18n.t('Success')} ${i18n.t('Step')}`, icon: 'fas fa-check-circle', action: 'addStep', subtype: 'success' },
    { text: `${i18n.t('Informational')} ${i18n.t('Step')}`, icon: 'fas fa-info-circle', action: 'addStep', subtype: 'informational' },
    { text: `${i18n.t('CodeStep')} ${i18n.t('Step')}`, icon: 'fas fa-code', action: 'addStep', subtype: 'code' },
    { text: `${i18n.t('CameraPicture')} ${i18n.t('Step')}`, icon: 'fas fa-camera', action: 'addStep', subtype: 'camera' },
    { text: 'Assign SOP', icon: 'fas fa-user-plus', action: 'addStep', subtype: 'assign-sop' },
    { text: 'Form Capture', icon: 'fas fa-clipboard-list', action: 'addStep', subtype: 'form-capture' },
    { type: 'separator' },
    { text: `Custom HTML Block`, icon: 'fas fa-box', action: 'addFreeHtml' },
    { text: i18n.t('Image'), icon: 'fas fa-image', action: 'addImage' },
    { text: i18n.t('Line'), icon: 'fas fa-minus', action: 'addLine' },
  ]
  
  menuItems.forEach(item => {
    if (item.type === 'separator') {
      // Create separator
      const separator = document.createElement('div')
      separator.style.height = '1px'
      separator.style.background = '#e0e0e0'
      separator.style.margin = '4px 0'
      separator.style.width = '100%'
      menu.appendChild(separator)
      return
    }
    
    const menuItem = document.createElement('div')
    menuItem.className = 'menu-item'
    menuItem.style.padding = '8px 12px'
    menuItem.style.cursor = 'pointer'
    menuItem.style.display = 'flex'
    menuItem.style.alignItems = 'center'
    menuItem.style.gap = '8px'
    menuItem.style.transition = 'background-color 0.2s ease'
    menuItem.innerHTML = `
      <i class="${item.icon}" style="width: 16px; text-align: center; color: #6c757d;"></i>
      ${item.text}
    `
    
    menuItem.addEventListener('mouseenter', () => {
      menuItem.style.backgroundColor = '#f8f9fa'
    })
    
    menuItem.addEventListener('mouseleave', () => {
      menuItem.style.backgroundColor = 'transparent'
    })
    
    menuItem.addEventListener('click', () => {
      // Emit event to EventBus
      EventBus.$emit(`${item.action}AtPosition`, { position, nodeType, subtype: item.subtype })
      document.body.removeChild(menu)
      document.removeEventListener('click', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    })
    
    menu.appendChild(menuItem)
  })
  
  // Add click outside listener
  const handleClickOutside = (e) => {
    // Check if click is outside the menu and not on any divider element
    const isOutsideMenu = !menu.contains(e.target)
    const isOnDivider = e.target.closest('.divider-menu') || e.target.closest('.divider-button') || e.target.closest('.divider-line')
    
    if (isOutsideMenu && !isOnDivider) {
      document.body.removeChild(menu)
      document.removeEventListener('click', handleClickOutside)
    }
  }
  
  // Add to DOM
  document.body.appendChild(menu)
  
  // Add event listener immediately
  document.addEventListener('click', handleClickOutside)
  
  // Also listen for escape key to close menu
  const handleEscape = (e) => {
    if (e.key === 'Escape') {
      document.body.removeChild(menu)
      document.removeEventListener('click', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }
  document.addEventListener('keydown', handleEscape)
}

export const DividerMenuExtension = Extension.create({
  name: 'dividerMenu',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('dividerMenu'),
        props: {
                  decorations: (state) => {
          const { doc } = state
          const decorations = []
          
                    // Find all draggable items and paragraphs
          const draggableItems = []
          const paragraphs = []
          doc.descendants((node, pos) => {
            if (node.type.name === 'draggableItem') {
              draggableItems.push({ node, pos })
            } else if (node.type.name === 'paragraph') {
              paragraphs.push({ node, pos })
            }
            return true
          })
          
          // Add divider before the first draggable item (step)
          if (draggableItems.length > 0) {
            const firstItem = draggableItems[0]
            const widget = document.createElement('div')
            widget.className = 'divider-menu'
            widget.innerHTML = `
              <div class="divider-line"></div>
              <div class="divider-button" style="pointer-events: auto;">
                <span class="plus-icon">+</span>
              </div>
            `
            
            // Only make the button clickable, not the line
            const button = widget.querySelector('.divider-button')
            button.addEventListener('click', (event) => {
              event.stopPropagation()
              showMenu(event, firstItem.pos, firstItem.node.type.name)
            })
            
            // Prevent clicks on the line from doing anything
            const line = widget.querySelector('.divider-line')
            line.style.pointerEvents = 'none'
            
            decorations.push(
              Decoration.widget(firstItem.pos, widget, {
                side: -1,
                key: `divider-before-first-step`
              })
            )
          }

          // Add dividers after all draggable items (steps)
          draggableItems.forEach((item) => {
            const widget = document.createElement('div')
            widget.className = 'divider-menu'
            widget.innerHTML = `
              <div class="divider-line"></div>
              <div class="divider-button" style="pointer-events: auto;">
                <span class="plus-icon">+</span>
              </div>
            `
            
            // Only make the button clickable, not the line
            const button = widget.querySelector('.divider-button')
            button.addEventListener('click', (event) => {
              event.stopPropagation()
              showMenu(event, item.pos + item.node.nodeSize, item.node.type.name)
            })
            
            // Prevent clicks on the line from doing anything
            const line = widget.querySelector('.divider-line')
            line.style.pointerEvents = 'none'
            
            decorations.push(
              Decoration.widget(item.pos + item.node.nodeSize, widget, {
                side: 1,
                key: `divider-step-${item.pos}`
              })
            )
          })
          
          // Add divider after the last content node that is not a step
          // Find all content nodes (paragraphs, images, etc.) that are not draggable items
          // AND are not inside draggable item nodes
          const contentNodes = []
          
          // First, get all draggable items to know their positions
          const draggableItemPositions = draggableItems.map(item => ({
            start: item.pos,
            end: item.pos + item.node.nodeSize
          }))
          
          // Find content nodes that are not inside any draggable item
          doc.descendants((node, pos) => {
            if (node.type.name !== 'draggableItem' && node.type.name !== 'doc') {
              // Check if this position is inside any draggable item
              const isInsideDraggableItem = draggableItemPositions.some(di => 
                pos >= di.start && pos < di.end
              )
              
              // Only add if not inside any draggable item
              if (!isInsideDraggableItem) {
                contentNodes.push({ node, pos })
              }
            }
            return true
          })
          
          // If there are content nodes and no draggable items, add divider after the last content node
          if (contentNodes.length > 0 && draggableItems.length === 0) {
            const lastContentNode = contentNodes[contentNodes.length - 1]
            const widget = document.createElement('div')
            widget.className = 'divider-menu'
            widget.innerHTML = `
              <div class="divider-line"></div>
              <div class="divider-button" style="pointer-events: auto;">
                <span class="plus-icon">+</span>
              </div>
            `
            
            // Only make the button clickable, not the line
            const button = widget.querySelector('.divider-button')
            button.addEventListener('click', (event) => {
              event.stopPropagation()
              showMenu(event, lastContentNode.pos + lastContentNode.node.nodeSize, lastContentNode.node.type.name)
            })
            
            // Prevent clicks on the line from doing anything
            const line = widget.querySelector('.divider-line')
            line.style.pointerEvents = 'none'
            
            decorations.push(
              Decoration.widget(lastContentNode.pos + lastContentNode.node.nodeSize, widget, {
                side: 1,
                key: 'divider-last-content'
              })
            )
          }
          
          // If there are both draggable items and content nodes, add divider after the last content node
          // but only if it comes after the last draggable item
          if (contentNodes.length > 0 && draggableItems.length > 0) {
            const lastDraggableItem = draggableItems[draggableItems.length - 1]
            const lastContentNode = contentNodes[contentNodes.length - 1]
            
            // Only add divider after last content node if it comes after the last draggable item
            if (lastContentNode.pos > lastDraggableItem.pos) {
              const widget = document.createElement('div')
              widget.className = 'divider-menu'
              widget.innerHTML = `
                <div class="divider-line"></div>
                <div class="divider-button" style="pointer-events: auto;">
                  <span class="plus-icon">+</span>
                </div>
              `
              
              // Only make the button clickable, not the line
              const button = widget.querySelector('.divider-button')
              button.addEventListener('click', (event) => {
                event.stopPropagation()
                showMenu(event, lastContentNode.pos + lastContentNode.node.nodeSize, lastContentNode.node.type.name)
              })
              
              // Prevent clicks on the line from doing anything
              const line = widget.querySelector('.divider-line')
              line.style.pointerEvents = 'none'
              
              decorations.push(
                Decoration.widget(lastContentNode.pos + lastContentNode.node.nodeSize, widget, {
                  side: 1,
                  key: 'divider-last-content'
                })
              )
            }
          }
          
          // If no items at all, add a divider at the end
          if (draggableItems.length === 0 && contentNodes.length === 0) {
            // Add a divider at the end of the document to avoid focus conflicts
            const widget = document.createElement('div')
            widget.className = 'divider-menu'
            widget.innerHTML = `
              <div class="divider-line"></div>
              <div class="divider-button" style="pointer-events: auto;">
                <span class="plus-icon">+</span>
              </div>
            `
            
            // Only make the button clickable, not the line
            const button = widget.querySelector('.divider-button')
            button.addEventListener('click', (event) => {
              event.stopPropagation()
              showMenu(event, doc.content.size, 'doc') // Position at the end of document
            })
            
            // Prevent clicks on the line from doing anything
            const line = widget.querySelector('.divider-line')
            line.style.pointerEvents = 'none'
            
            decorations.push(
              Decoration.widget(doc.content.size, widget, {
                side: 1,
                key: 'divider-end'
              })
            )
          }
          
          return DecorationSet.create(doc, decorations)
        }
        }
      })
    ]
  }
})

